import * as Sentry from "@sentry/nextjs";
import { initSentry } from "./init";

// Helper functions for noise filtering
function looksLikeChunk(url: string) {
  return /\/_next\/static\/.+\.(?:js|css)(?:$|\?)/i.test(url);
}
function looksLikeMedia(url: string) {
  return /\.(?:png|jpe?g|webp|gif|svg|mp4|webm|mov)(?:$|\?)/i.test(url);
}
function looksLikeRSC(url: string) {
  return /[?&]_?rsc=/.test(url);
}

// Define breadcrumb shape for type safety
type Breadcrumb = { data?: Record<string, unknown>; message?: string };

/**
 * Initialize Sentry for client-side with shared filtering logic.
 * Includes "TypeError: Load failed" noise reduction for Safari/WebKit.
 */
export function initClientSentry(options: {
  dsn?: string;
  enabled?: boolean;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}) {
  const {
    // DSN must be available client-side (e.g., via NEXT_PUBLIC_* or build injection)
    dsn = process.env.NEXT_PUBLIC_SENTRY_DSN,
    enabled = true,
    replaysSessionSampleRate = 0.1,
    replaysOnErrorSampleRate = 0.5,
  } = options;

  return initSentry({
    dsn,
    enabled,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    // Disable transaction tracing to reduce overhead and cost
    tracesSampleRate: 0,

    // Client-specific beforeSend for "TypeError: Load failed" noise reduction
    beforeSend(event: unknown, hint?: unknown) {
      try {
        const ex = (event as { exception?: { values?: unknown[] } }).exception
          ?.values?.[0] as
          | {
              mechanism?: { type?: string };
              type?: string;
              value?: unknown;
              stacktrace?: { frames?: unknown[] };
            }
          | undefined;
        const mech = ex?.mechanism?.type;
        const type = ex?.type ?? "";
        const value = String(ex?.value ?? "");
        const frames = ex?.stacktrace?.frames ?? [];
        const hasStack = Array.isArray(frames) && frames.length > 0;

        // Gather resource URL from breadcrumbs (shared package enriches these)
        const crumbs: Breadcrumb[] = Array.isArray(
          (event as { breadcrumbs?: unknown }).breadcrumbs,
        )
          ? (event as { breadcrumbs?: Breadcrumb[] }).breadcrumbs || []
          : [];

        let resourceUrl: string | undefined;
        for (const c of crumbs) {
          const u =
            (c?.data?.resourceUrl as string) ||
            (c?.data?.url as string) ||
            (typeof c?.message === "string" &&
              c.message.startsWith("GET ") &&
              c.message.split(" ")[1]);
          if (typeof u === "string") {
            resourceUrl = u;
            if (c?.data?.resourceUrl) break;
          }
        }

        const isLoadFailed =
          mech === "onunhandledrejection" &&
          type === "TypeError" &&
          value === "Load failed";

        if (isLoadFailed && !hasStack) {
          const u = resourceUrl || "";
          const isChunk = u && looksLikeChunk(u);
          const isMedia = u && looksLikeMedia(u);
          const isRSC = u && looksLikeRSC(u);

          // Keep only chunk/CSS failures; drop media/RSC noise
          if (!isChunk) {
            if (process.env.NODE_ENV !== "production") {
              console.info("[Sentry] Suppressed 'Load failed'", {
                resourceUrl: u || "(none)",
                reason: isMedia ? "media" : isRSC ? "rsc" : "no-useful-url",
              });
            }
            return null; // DROP
          } else {
            // Annotate kept events
            const e = event as Record<string, unknown>;
            e.extra = {
              ...((e.extra as Record<string, unknown> | undefined) || {}),
              loadFailedResourceUrl: u,
              keptBecause: "looks-like-next-chunk-or-css",
            };
          }
        }

        // Attach original rejection reason if available
        const original = (hint as { originalException?: unknown } | undefined)
          ?.originalException;
        if (
          original &&
          typeof (original as { reason?: unknown }).reason !== "undefined"
        ) {
          const r = (original as { reason?: unknown }).reason;
          const e = event as Record<string, unknown>;
          e.extra = {
            ...((e.extra as Record<string, unknown> | undefined) || {}),
            unhandledRejectionReason:
              typeof r === "string"
                ? r
                : ((r && (r as { message?: unknown }).message) ?? String(r)),
          };
        }
      } catch {
        // Never block reporting
      }

      return event;
    },
  });
}
